import { AdvisoryEngineLabel } from '../AdvisoryEngineLabel';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { AppDispatch } from '../../../../redux/store';
import { getValidToken } from '../../../../redux/slices/userSlice';
import { useUntypedTranslation } from '../../../../hooks/useUntypedTranslation';
import { useAdvisoryWorkspace } from '../../../../hooks/advisory/useAdvisoryWorkspace';
import { isEvidenceGap, type WorkspaceSource } from '../../../../hooks/advisory/advisoryWorkspace';
import type {
  SupervisorTurnOutcome,
  SupervisorTurnRequest,
} from '../../../../hooks/advisory/useSupervisorTurn';
import {
  uploadWorkspaceFile,
  validateWorkspaceFile,
  WorkspaceUploadError,
} from '../../../../services/advisoryWorkspaceUpload';
import { meta, focusRing, pressable } from '../../../../styles/visualLanguage';
import { AdvisoryKeyQuestionEditor } from '../AdvisoryKeyQuestionEditor';
import { AdvisoryEngagementWorkspace } from './AdvisoryEngagementWorkspace';
import { backgroundHint, sourceProgress, taskStatus } from './advisoryWorkspaceProgress';
import type {
  AdvisoryWorkspaceSourcesProps,
  AdvisoryWorkspaceStage,
  AdvisoryWorkspaceStatus,
} from './advisoryWorkspaceTypes';

type UploadReceipt = {
  requestId: string;
  generation: number;
  name: string;
  size: number;
  type: string;
};
/** Live controller for enrolled engagements. All writes are revisioned supervisor commands. */
export function AdvisoryWorkspaceLive({
  userId,
  projectId,
  send,
  explorationPanel,
  answerPanel,
  answerReady = false,
}: {
  userId: string;
  projectId: string;
  send: (request: SupervisorTurnRequest) => Promise<SupervisorTurnOutcome>;
  explorationPanel?: React.ReactNode;
  answerPanel?: React.ReactNode | ((runId: string | null) => React.ReactNode);
  /** The completed brief can replace the preparation and generation workspace. */
  answerReady?: boolean;
}) {
  const { t } = useUntypedTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const workspace = useAdvisoryWorkspace({ userId, projectId, send });
  const snapshot = workspace.snapshot;
  const [selected, setSelected] = useState<AdvisoryWorkspaceStage>('companies');
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const initialSelection = useRef(false);
  const [editor, setEditor] = useState<AdvisoryWorkspaceSourcesProps['editor']>(null);
  const submittedUrlDraft = useRef<AdvisoryWorkspaceSourcesProps['editor']>(null);
  const [questionPending, setQuestionPending] = useState(false);
  const [acknowledged, setAcknowledged] = useState<Record<string, number>>({});
  const [uploadBusy, setUploadBusy] = useState(false);
  const uploadInFlight = useRef(false);
  const [fileBatchBusy, setFileBatchBusy] = useState(false);
  const fileBatchInFlight = useRef(false);
  const [validatingFiles, setValidatingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const uploadKey = (sourceId: string) =>
    `advisory-workspace-upload:${userId}/${projectId}/${sourceId}`;
  useEffect(() => {
    if (snapshot && !initialSelection.current) {
      initialSelection.current = true;
      if (snapshot.locked) setSelected(snapshot.tasks.answers ? 'answers' : 'exploration');
    }
  }, [snapshot]);
  const unavailable =
    workspace.busy ||
    Boolean(workspace.pending) ||
    workspace.stale ||
    !workspace.authoritative ||
    workspace.readStatus !== 'ready' ||
    uploadBusy ||
    fileBatchBusy;
  const clearSavedUrlDraft = (draft: AdvisoryWorkspaceSourcesProps['editor']) => {
    if (!draft || !alive.current) return;
    setEditor(current => (current === draft ? null : current));
    if (submittedUrlDraft.current === draft) submittedUrlDraft.current = null;
  };
  const retryPending = async () => {
    const pending = workspace.pending;
    const draft = submittedUrlDraft.current;
    const result = await workspace.retry();
    if (
      result &&
      pending?.operation === 'addUrl' &&
      draft?.url.trim() === pending.url &&
      draft.companyId === pending.company
    )
      clearSavedUrlDraft(draft);
  };
  const upload = async (
    source: Pick<
      WorkspaceSource,
      'sourceId' | 'generation' | 'fileName' | 'sizeBytes' | 'mimeType'
    >,
    file: File
  ) => {
    if (uploadInFlight.current || snapshot?.locked) return;
    if (
      file.name !== source.fileName ||
      file.size !== source.sizeBytes ||
      (file.type || 'application/octet-stream') !== source.mimeType
    ) {
      setUploadError('advisory:workspace.live.fileMismatch');
      return;
    }
    let receipt: UploadReceipt;
    try {
      const saved = JSON.parse(
        sessionStorage.getItem(uploadKey(source.sourceId)) ?? 'null'
      ) as UploadReceipt | null;
      receipt =
        saved?.generation === source.generation
          ? saved
          : {
              requestId: uuidv4(),
              generation: source.generation,
              name: file.name,
              size: file.size,
              type: file.type || 'application/octet-stream',
            };
      if (
        receipt.name !== file.name ||
        receipt.size !== file.size ||
        receipt.type !== (file.type || 'application/octet-stream')
      ) {
        setUploadError('advisory:workspace.live.fileMismatch');
        return;
      }
      sessionStorage.setItem(uploadKey(source.sourceId), JSON.stringify(receipt));
    } catch {
      setUploadError('advisory:workspace.live.storageFailed');
      return;
    }
    uploadInFlight.current = true;
    setUploadBusy(true);
    setUploadError(null);
    try {
      const token = await dispatch(getValidToken()).unwrap();
      await uploadWorkspaceFile(
        projectId,
        source.sourceId,
        source.generation,
        receipt.requestId,
        file,
        token
      );
      sessionStorage.removeItem(uploadKey(source.sourceId));
    } catch {
      if (alive.current) setUploadError('advisory:workspace.live.uploadFailed');
    } finally {
      uploadInFlight.current = false;
      if (alive.current) setUploadBusy(false);
    }
  };
  const addFiles = async (files: File[], companyId: string | null) => {
    if (unavailable || snapshot?.locked || fileBatchInFlight.current || uploadInFlight.current)
      return;
    fileBatchInFlight.current = true;
    setFileBatchBusy(true);
    setValidatingFiles(true);
    setUploadError(null);
    try {
      const token = await dispatch(getValidToken()).unwrap();
      // Check the whole selection against the gateway's policy before reserving sources.
      for (const file of files) {
        await validateWorkspaceFile(file, token);
        if (!alive.current) return;
      }
      setValidatingFiles(false);
      // One reservation/upload at a time avoids reusing a stale setup revision.
      for (const file of files) {
        const mimeType = file.type || 'application/octet-stream';
        const result = await workspace.execute({
          operation: 'registerFile',
          fileName: file.name,
          sizeBytes: file.size,
          mimeType,
          company: companyId,
        });
        if (!result?.sourceId || !result.generation || !alive.current) break;
        await upload(
          {
            sourceId: result.sourceId,
            generation: result.generation,
            fileName: file.name,
            sizeBytes: file.size,
            mimeType,
          },
          file
        );
        if (!alive.current) break;
      }
    } catch (error) {
      if (alive.current)
        setUploadError(
          error instanceof WorkspaceUploadError
            ? error.i18nKey
            : 'advisory:workspace.live.fileValidationFailed'
        );
    } finally {
      fileBatchInFlight.current = false;
      if (alive.current) {
        setFileBatchBusy(false);
        setValidatingFiles(false);
      }
    }
  };
  const recovery = (
    <Box aria-live="polite" sx={{ mb: 2 }}>
      {workspace.readStatus !== 'ready' && (
        <Box>
          <Typography sx={[meta]}>
            {t('advisory:workspace.live.read.' + workspace.readStatus)}
          </Typography>
          <Button onClick={workspace.reload} sx={[pressable, focusRing]}>
            {t('advisory:workspace.live.reload')}
          </Button>
        </Box>
      )}
      {workspace.error && (
        <Typography role="alert" sx={[meta]}>
          {t(workspace.error)}
        </Typography>
      )}
      {workspace.pending && (
        <Button
          disabled={workspace.busy}
          onClick={() => void retryPending()}
          sx={[pressable, focusRing]}
        >
          {t('advisory:workspace.live.retryAction')}
        </Button>
      )}
      {workspace.stale && (
        <Box>
          <Typography sx={[meta]}>{t('advisory:workspace.live.changedElsewhere')}</Typography>
          <Button
            disabled={workspace.busy || !workspace.authoritative}
            onClick={workspace.acceptLatest}
            sx={[pressable, focusRing]}
          >
            {t('advisory:workspace.live.acceptLatest')}
          </Button>
        </Box>
      )}
      {uploadError && (
        <Typography role="alert" sx={[meta]}>
          {t(uploadError)}
        </Typography>
      )}
      {uploadBusy && (
        <Typography role="status" sx={[meta]}>
          {t('advisory:workspace.live.uploading')}
        </Typography>
      )}
      {validatingFiles && (
        <Typography role="status" sx={[meta]}>
          {t('advisory:workspace.live.validatingFiles')}
        </Typography>
      )}
    </Box>
  );
  if (!snapshot)
    return (
      <Box data-testid="workspace-loading" sx={{ p: 3 }}>
        {recovery}
      </Box>
    );
  const resolvedAnswerPanel =
    typeof answerPanel === 'function' ? answerPanel(snapshot.run?.runId ?? null) : answerPanel;
  if (answerReady && resolvedAnswerPanel) {
    return (
      <Box data-testid="advisory-workspace-live">
        <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2.5, sm: 4.5 } }}>
          <AdvisoryEngineLabel engine={snapshot.run?.engine ?? snapshot.engine} />
        </Box>
        {resolvedAnswerPanel}
      </Box>
    );
  }
  const sourceValues = Object.values(snapshot.sources);
  const orderedSources = [...sourceValues].sort(
    (a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) ||
      a.sourceId.localeCompare(b.sourceId)
  );
  const profiles = orderedSources.filter(source => source.kind === 'profile');
  const progress = (source: WorkspaceSource) => sourceProgress(source, snapshot.tasks, nowMs);
  const acceptedGap = (source: WorkspaceSource) =>
    snapshot.locked &&
    isEvidenceGap(source.status) &&
    snapshot.acceptedGaps.some(
      gap => gap.sourceId === source.sourceId && gap.generation === source.generation
    );
  const profileStatus = (source: WorkspaceSource): AdvisoryWorkspaceStatus =>
    acceptedGap(source) ? 'withGaps' : progress(source).status;
  const gaps = sourceValues.filter(source => isEvidenceGap(source.status));
  const acknowledgedGaps = gaps
    .filter(source => acknowledged[source.sourceId] === source.generation)
    .map(source => ({ sourceId: source.sourceId, generation: source.generation }));
  const gapReady = acknowledgedGaps.length === gaps.length;
  const companiesStatus: AdvisoryWorkspaceStatus = profiles.some(
    source => profileStatus(source) === 'running'
  )
    ? 'running'
    : profiles.some(source => profileStatus(source) === 'slow')
    ? 'slow'
    : profiles.some(source => profileStatus(source) === 'needsInput')
    ? 'needsInput'
    : profiles.some(source => profileStatus(source) === 'withGaps')
    ? 'withGaps'
    : 'ready';
  const blockers = [
    ...(!snapshot.questionsReady ? ['advisory:workspace.live.questionsPreparing'] : []),
    ...(!snapshot.questions.length && snapshot.questionsReady
      ? ['advisory:workspace.live.noQuestions']
      : []),
    ...(snapshot.blockers.some(b => b.code === 'evidencePending')
      ? ['advisory:workspace.blocker.sources']
      : []),
    ...(!gapReady ? ['advisory:workspace.blocker.gaps'] : []),
    ...(questionPending ? ['advisory:workspace.blocker.questions'] : []),
    ...(editor?.url.trim() ? ['advisory:workspace.blocker.unsavedSource'] : []),
    ...(unavailable ? ['advisory:workspace.live.waitingForSave'] : []),
  ];
  const resumeControls = (kind: 'initialize' | 'exploration' | 'answers') =>
    Object.values(snapshot.tasks)
      .filter(
        task =>
          task.kind === kind &&
          (kind === 'initialize'
            ? ['failed', 'needsInput'].includes(task.status)
            : task.status === 'queued' &&
              ['connectionInterrupted', 'executionInterrupted', 'workerInterrupted'].includes(
                task.errorCode ?? ''
              )) &&
          !['submissionUncertain', 'ingestionUncertain'].includes(task.errorCode ?? '')
      )
      .map(task => (
        <Button
          key={task.taskId}
          disabled={unavailable}
          onClick={() => void workspace.execute({ operation: 'resumeTask', taskId: task.taskId })}
          sx={[pressable, focusRing]}
        >
          {t('advisory:workspace.live.resumeTask')}
        </Button>
      ));
  const stageWarning = (kind: 'initialize' | 'exploration' | 'answers') => {
    const task = Object.values(snapshot.tasks).find(item => item.kind === kind);
    const hint = backgroundHint(task);
    return hint ? (
      <Typography
        role="status"
        sx={[meta, theme => ({ color: theme.colors.reviewWarning, my: 2 })]}
      >
        {t(hint)}
      </Typography>
    ) : null;
  };
  return (
    <Box data-testid="advisory-workspace-live">
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2.5, sm: 4.5 } }}>
        <AdvisoryEngineLabel engine={snapshot.run?.engine ?? snapshot.engine} />
        {recovery}
      </Box>
      <AdvisoryEngagementWorkspace
        goal={snapshot.goal}
        selectedStage={selected}
        onSelectStage={setSelected}
        stages={{
          companies: companiesStatus,
          exploration: taskStatus(snapshot.tasks.exploration),
          answers: taskStatus(snapshot.tasks.answers),
        }}
        companies={profiles.map(source => ({
          id: source.company ?? source.sourceId,
          name: source.company ?? source.name,
          status: profileStatus(source),
          hintKey: acceptedGap(source) ? undefined : progress(source).hintKey,
        }))}
        sources={orderedSources.map(source => ({
          id: source.sourceId,
          name: source.name,
          companyId: source.company,
          kind: source.kind === 'url' ? 'page' : source.kind,
          status:
            source.status === 'ready'
              ? 'ready'
              : isEvidenceGap(source.status)
              ? 'failed'
              : 'processing',
          statusKey: progress(source).statusKey,
          attention: progress(source).status === 'slow',
          url: source.finalUrl ?? source.submittedUrl,
          retryable: isEvidenceGap(source.status),
          retryLabelKey:
            source.status === 'ingestionFailed'
              ? 'advisory:workspace.live.retryIngestion'
              : source.kind === 'profile'
              ? 'advisory:workspace.live.retryResearch'
              : source.kind === 'url' && source.errorCode === 'sourceUnavailable'
              ? 'advisory:workspace.live.retryRetrieval'
              : undefined,
          needsFile:
            source.kind === 'file' && ['awaitingUpload', 'uploading'].includes(source.status),
        }))}
        gaps={orderedSources
          .filter(source => isEvidenceGap(source.status))
          .map(source => ({
            sourceId: source.sourceId,
            generation: source.generation,
            label: source.name,
            companyId: source.company,
            acknowledged: snapshot.locked
              ? acceptedGap(source)
              : acknowledged[source.sourceId] === source.generation,
          }))}
        editor={editor ? { ...editor, busy: unavailable } : null}
        locked={snapshot.locked}
        busy={unavailable}
        onOpenSources={companyId => {
          if (!unavailable)
            setEditor(old => ({ companyId, url: old?.companyId === companyId ? old.url : '' }));
        }}
        onCloseSources={() => setEditor(null)}
        onUrlChange={url => setEditor(old => (old ? { ...old, url, errorKey: undefined } : null))}
        onAddUrl={() => {
          if (!editor || unavailable) return;
          const saving = editor;
          submittedUrlDraft.current = saving;
          void workspace
            .execute({ operation: 'addUrl', url: saving.url.trim(), company: saving.companyId })
            .then(result => {
              if (result) clearSavedUrlDraft(saving);
            });
        }}
        onAddFiles={(files, companyId) => void addFiles(files, companyId)}
        onResumeUpload={(sourceId, file) => {
          const source = snapshot.sources[sourceId];
          if (source && !unavailable && !fileBatchInFlight.current) void upload(source, file);
        }}
        onRetrySource={sourceId => {
          const source = snapshot.sources[sourceId];
          if (!source || unavailable) return;
          void workspace.execute(
            source.kind === 'profile' && source.status !== 'ingestionFailed'
              ? { operation: 'retryProfile', company: source.company ?? source.name }
              : { operation: 'retrySource', sourceId }
          );
        }}
        onAcknowledgeGap={(sourceId, generation, checked) =>
          setAcknowledged(old => ({ ...old, [sourceId]: checked ? generation : 0 }))
        }
        questionPanel={
          <>
            {stageWarning('initialize')}
            {resumeControls('initialize')}
            <AdvisoryKeyQuestionEditor
              embedded
              readOnly={snapshot.locked}
              busy={unavailable}
              onPendingChange={setQuestionPending}
              projectId={projectId}
              keyQuestions={snapshot.questions}
              channelState={snapshot.questionsReady ? 'ready' : 'loading'}
              channelErrorCode={null}
              orphaned={{}}
              resolvedAddQuery={snapshot.questionProposals?.userQuery}
              proposals={
                snapshot.questionProposals
                  ? {
                      ...snapshot.questionProposals,
                      proposals: snapshot.questionProposals.proposals.filter(
                        proposal =>
                          !snapshot.questions.some(question => question.id === proposal.id)
                      ),
                    }
                  : null
              }
              onRemove={async question =>
                (await workspace.execute({
                  operation: 'removeQuestions',
                  questionIds: [question.id],
                }))
                  ? null
                  : 'advisory:workspace.live.actionFailed'
              }
              onAdd={async query =>
                (await workspace.execute({ operation: 'matchQuestion', query }))
                  ? null
                  : 'advisory:workspace.live.actionFailed'
              }
              onAccept={async questionId =>
                (await workspace.execute({
                  operation: 'acceptQuestion',
                  questionId,
                  query: snapshot.questionProposals?.userQuery ?? '',
                }))
                  ? null
                  : 'advisory:workspace.live.actionFailed'
              }
              onBack={() => undefined}
              onConfirm={() => undefined}
              onRetryChannel={workspace.reload}
            />
          </>
        }
        explorationPanel={
          <>
            {explorationPanel ?? (
              <Typography sx={[meta]}>{t('advisory:workspace.waitingProgress')}</Typography>
            )}
            {stageWarning('exploration')}
            {resumeControls('exploration')}
          </>
        }
        answerPanel={
          <>
            {snapshot.acceptedGaps.length > 0 && (
              <Box role="note" sx={{ mb: 2 }}>
                <Typography sx={[meta]}>{t('advisory:workspace.live.acceptedGaps')}</Typography>
                <Box component="ul">
                  {snapshot.acceptedGaps.map(gap => (
                    <Typography component="li" key={gap.sourceId} sx={[meta]}>
                      {gap.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {resolvedAnswerPanel ?? (
              <Typography sx={[meta]}>{t('advisory:workspace.answerHint')}</Typography>
            )}
            {stageWarning('answers')}
            {resumeControls('answers')}
          </>
        }
        startBlockers={blockers}
        canStart={
          !unavailable &&
          !snapshot.locked &&
          snapshot.questions.length > 0 &&
          (snapshot.canStart || (snapshot.canStartWithGaps && gapReady))
        }
        started={snapshot.locked}
        startPending={workspace.busy && workspace.pending?.operation === 'start'}
        onStart={() => {
          if (unavailable) return;
          void workspace.execute({ operation: 'start', acknowledgedGaps }).then(result => {
            if (result && alive.current) setSelected('exploration');
          });
        }}
      />
    </Box>
  );
}
